import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { mergeStyleSets, List, DirectionalHint } from '@fluentui/react';
import { runCancelableAction, runShortAction } from 'src/services/utils';
import { CommandsBar } from 'src/components/base/CommandIcon';
import { ListItem } from 'src/components/base/ListItem';
import { useModifiedItems, Direction, getEditModeButtons, matchesToProvider } from 'src/components/Flows/utils';
import { TeachingCoachmark } from 'src/components/base/TeachingCoachmark';
import { useIdAsTarget } from 'shared/hooks'
import {
  Type, toDisplayName, getRoute, InsertFlowsteps,
  DeleteFlowsteps, toStrings, AdminToolFlowsteps
} from 'shared/utils/flowstep';
import { getRoute as getV2Route } from 'src/v2/services/flowsteps/routing';
import * as Flow from 'shared/utils/flow';
import { dataStore, officeStore, userStore } from 'src/stores';
import office from 'src/services/office';
import { isNewFlowstepFormat } from 'shared/types/flowstep';
import { Operation, AccountType, ProviderType } from 'shared/types/flowstep';
import { appState } from "@v2/app-state";
import { ExecutionConfirmationDialog } from "@v2/components/dialogs/ExecutionConfirmationDialog/ExecutionConfirmationDialog";


const classNames = mergeStyleSets({
  flowstepsList: {
    width: '100%',
    minHeight: '100px',
  },
});


export function FlowstepList({
  selectedFlow,
  anyEditModeEnabled,
  onlyOnActiveWorksheet,
  onEditModeEnabled,
  activeWorksheetId,
  license,
}) {
  const {
    resetItems: resetModifiedItems,
    removeItem: handleRemove,
    moveItem: handleMove,
    items: editModeFlowsteps,
  } = useModifiedItems([], (data) => data?.flowstep.id);
  const [editModeEnabled, setEditModeEnabled] = useState(false);
  const [listId, listTarget] = useIdAsTarget('list');

  const history = useHistory();
  const selectedFlowId = selectedFlow?.flow.id;
  const visibleData = selectedFlow ? selectedFlow.flowsteps : [];

  const visibleFlowsteps = useMemo(() => {
      const flowsteps =  editModeEnabled ? editModeFlowsteps.modified : visibleData;
      const providerFlowsteps = flowsteps.filter(data => matchesToProvider(data.flowstep, userStore.accountType));
      return onlyOnActiveWorksheet
        ? providerFlowsteps.filter(x => x.flowstep.worksheetId === activeWorksheetId)
        : [...providerFlowsteps]; // Force update when anyEditModeEnabled changes
  },
  [
    activeWorksheetId,
    onlyOnActiveWorksheet,
    anyEditModeEnabled,
    selectedFlow,
    editModeEnabled,
    editModeFlowsteps.modified,
  ]);

  function handleEditModeClick() {
    resetModifiedItems(visibleData);
    handleEditMode(true);
  }

  function handleEditMode(isEnabled) {
    setEditModeEnabled(isEnabled);
    onEditModeEnabled(isEnabled);
  }

  async function handleEditModeApply() {
    const { modified, areMoved, removed } = editModeFlowsteps;
    await runShortAction(async () => {
      const areRemoved = !!removed.length;
      areRemoved && await dataStore.deleteFlowstep(removed.map(x => x.flowstep), selectedFlowId);

      if (areMoved || areRemoved) {
        const flowsteps = modified.map(x => ({ id: x.flowstep.id }));
        const { flow } = selectedFlow;
        const newFlow = { ...flow, options: { ...flow.options, flowsteps } };
        await dataStore.createOrUpdateFlow(newFlow)
      }
    });

    handleEditMode(false);
  }

  function handleEditModeReset() {
    handleEditMode(false);
  }

  // Flowsteps toolbar
  const getFlowstepsToolbar = () => {
    if (!selectedFlow) {
      return { leftItems: [null], rightItems: [] };
    }

    if (editModeEnabled) {
      return {
        leftItems: [{ iconName: 'Cancel', tooltip: 'Reset all changes', onClick: handleEditModeReset }],
        rightItems: [{ iconName: 'CheckMark', tooltip: 'Apply changes', onClick: handleEditModeApply, }]
      };
    }

    const { type } = selectedFlow.flow;
    const { flowsteps, flows, adminTools } = license;
    const { push, pull } = flowsteps;

    const isCustomEditableType = Flow.Type.Custom === type && flows.custom.addFlowsteps.can;
    const canAddPulls = type === Flow.Type.Pull || isCustomEditableType;
    const canAddPushes = (type === Flow.Type.Push && push.createOrRemove.unlimited.can) || isCustomEditableType;

    const newItems = (userStore.accountType === AccountType.HS
      ? [
        canAddPulls && [
          {
            text: 'Pull from Objects',
            operation: Operation.PULL,
            provider: ProviderType.HS,
          },
          {
            text: 'Pull from Lists',
            operation: Operation.PULL,
            provider: ProviderType.HubspotList,
          }
        ],
        canAddPushes && [{
          text: 'Insert',
          operation: Operation.INSERT,
          provider: ProviderType.HS,
        }, {
          text: 'Update',
          operation: Operation.UPDATE,
          provider: ProviderType.HS,
        }],
        canAddPushes && push.massPush.deleteOrRestore.can && [{
          text: 'Delete',
          operation: Operation.DELETE,
          provider: ProviderType.HS,
        }],
      ]
      : [
        canAddPulls && [
          pull.createOrRemove.report.can && Type.Report,
          pull.createOrRemove.soql.can && Type.Soql,
          pull.createOrRemove.csv.can && Type.Csv,
          pull.createOrRemove.sosl.can && Type.Sosl
        ].filter(Boolean),
        canAddPushes && InsertFlowsteps.filter(step => push.massPush[step].can),
        canAddPushes && push.massPush.deleteOrRestore.can && DeleteFlowsteps,
        canAddPushes && adminTools.seeHomeButton.can && AdminToolFlowsteps,
      ])
      .filter(Boolean)
      .reduce((out, x, index, self) => {
        out.push(...x);
        index !== self.length - 1 && out.push(null); // Separator
        return out;
      }, []);

    const leftItems = (!anyEditModeEnabled
      ? [newItems.length &&
      {
        iconName: 'xappex-new-datasource',
        tooltip: 'Create new flow step',
        menuItems: toCommandItems(newItems),
        teaching: { id: 'flows-add-flowstep' },
      },
      (canAddPulls || canAddPushes) &&
      {
        iconName: 'xappex-edit',
        tooltip: 'Turn on edit mode',
        onClick: handleEditModeClick,
        teaching: { id: 'flows-edit-flowsteps' },
      },
      ]
      : []).filter(Boolean);

    return { leftItems, rightItems: [null] };
  }

  const flowstepsToolbar = getFlowstepsToolbar();
  const teaching = { id: 'flows-flowsteps-list', directionalHint: DirectionalHint.topCenter };

  // Render misc
  function toCommandItems(items) {
    return items.map(item => {
      const { type, text } = typeof (item) === 'string'
        ? { type: item, text: toDisplayName(item) }
        : (item || {});
      return !!text
        ? {
          key: text, text,
          onClick: () => history.push(
            userStore.accountType === AccountType.HS
              ? getV2Route(null, item.operation, item.provider, { createNew: 'true' })
              : getRoute({
                type, createNew: true,
                flowId: selectedFlowId
              }))
        }
        : null;
    })
  }

  return (
    <>
      <CommandsBar {...flowstepsToolbar} />
      <List
        id={listId}
        className={classNames.flowstepsList}
        items={visibleFlowsteps}
        onRenderCell={(item, index) =>
          <FlowstepItem
            key={item.flowstep.id}
            data={item}
            flowId={selectedFlowId}
            isLast={index === visibleFlowsteps.length - 1}
            isFirst={!index}
            onlyOnActiveWorksheet={onlyOnActiveWorksheet}
            editModeEnabled={editModeEnabled}
            anyEditModeEnabled={anyEditModeEnabled}
            license={license}
            onMoved={handleMove}
            onRemoved={handleRemove}
          />
        }
      />
      {teaching && <TeachingCoachmark target={listTarget} {...teaching} />}
    </>
  );
}


function FlowstepItem({ data, flowId, isFirst, isLast, onlyOnActiveWorksheet, editModeEnabled, anyEditModeEnabled, license, onRemoved, onMoved }) {
  const history = useHistory();

  const { seeRunButton } = license.flowsteps;
  const { flowstep } = data;
  const { type, id, worksheetId, target, category } = flowstep;

  const getConfirmation = async () => {
    const isPushFlowstep = () => [
      Operation.UPDATE,
      Operation.DELETE,
      Operation.INSERT
    ].includes(flowstep.originalFlowstep?.operation);

    if (!isNewFlowstepFormat(flowstep) || !isPushFlowstep()) {
      return true;
    }

    return await appState.showDialog((setResult) =>
      <ExecutionConfirmationDialog
        onResult={(value) => setResult(value)}
        flowstep={flowstep.originalFlowstep}
      />
    );
  }

  async function handleRun() {
    const isConfirmed = await getConfirmation();

    if (isConfirmed) {
      await runCancelableAction(async (cancelToken) => {
        await officeStore.executeFlowstep(flowstep, cancelToken);
      });
    }
  }

  async function handleGoToWorksheet() {
    await runShortAction(async () => {
      await office.activateWorksheet(worksheetId, target);
    });
  }

  function handleClick() {
    if (!anyEditModeEnabled) {
      history.push(isNewFlowstepFormat(flowstep)
        ? getV2Route(flowstep.id, flowstep.originalFlowstep.operation, flowstep.originalFlowstep.provider)
        : getRoute({ type, id, flowId, category }))
    }
  }

  function handleMoved(direction) {
    onMoved(data, direction);
  }

  function handleRemoved() {
    onRemoved(data);
  }

  function getTitles() {
    const { worksheet } = data;

    const { name: title, target, type: typeName, description, errorReaction, pullMode, object } = toStrings(flowstep, worksheet ? worksheet.name : flowstep.name);
    const subtitle = [`${target} - ${typeName} ${description}`, pullMode || object || errorReaction];
    return { title, subtitle };
  }

  const titles = useMemo(getTitles, [data]);
  const buttons = editModeEnabled
    ? getEditModeButtons({ isFirst, isLast, onMoveClick: handleMoved, onDeleteClick: handleRemoved })
    : anyEditModeEnabled
      ? [null] // Placeholder
      : [
        !onlyOnActiveWorksheet && { iconName: 'xappex-current-worksheet', tooltip: `Activate worksheet this flow step is associated with`, onClick: handleGoToWorksheet },
        seeRunButton.can && { iconName: 'xappex-run-flowstep', tooltip: 'Run', onClick: handleRun },
      ].filter(Boolean);

  const titleStyle = { title: { textDecoration: data.worksheet ? '' : 'line-through' } }
  const props = {
    ...titles, buttons, onClick: handleClick,
    styles: anyEditModeEnabled ? titleStyle : { root: { cursor: 'pointer' }, ...titleStyle }
  };

  return <ListItem {...props} />;
}


export default FlowstepList;
